import { FolderIcon } from "@/Icons/v2/Library/FolderIcon";
import { useTerminalsState } from "@/States/Terminals";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { isSafeFileName } from "@/pages/library_v2/components/filesView/utils";
import { useTrans } from "@/translations";

import { useCallback, useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";

type Props = {
  selectedFolderName: string;
  setSelectedFolderName: (value: string) => void;
};

export const FoldersSelect: React.FC<Props> = ({
  selectedFolderName,
  setSelectedFolderName,
}) => {
  const { t } = useTrans();
  const { currentTerminal } = useTerminalsState();
  const { setGlobalAlert } = useGlobalAlert();
  const trpcUtils = trpc.useUtils();
  const [newFolderName, setNewFolderName] = useState("");

  const isNewFolderNameValid = useMemo(
    () => isSafeFileName(newFolderName),
    [newFolderName],
  );

  const { data: folders = [] } = trpc.library.getFolders.useQuery({
    terminalId: currentTerminal?.id!,
  });

  const { mutate: createFolder } = trpc.library.createFolder.useMutation({
    onSuccess: () =>
      trpcUtils.library.getFolders.invalidate({
        terminalId: currentTerminal?.id!,
      }),
    onError: (error) => {
      setGlobalAlert({
        type: "error",
        message: "errors:documentDeleteFailed",
      });
      console.error("Error creating folder:", error);
    },
  });

  const onAddFolder = useCallback(() => {
    const trimmed = newFolderName.trim();
    if (!trimmed || !isSafeFileName(trimmed)) return;
    setNewFolderName("");

    createFolder({
      terminalId: currentTerminal?.id!,
      folderName: trimmed,
    });
    setSelectedFolderName(trimmed);
  }, [newFolderName, createFolder, currentTerminal?.id, setSelectedFolderName]);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-grey6 text-[12px] font-medium">
        {t("libraryPage:fileUploadFolder")}
      </span>
      <div className="flex flex-wrap gap-[6px]">
        {folders.map((f) => {
          const active = selectedFolderName === f.name;
          return (
            <button
              key={f.name}
              type="button"
              onClick={() => setSelectedFolderName(f.name)}
              className={twMerge(
                "inline-flex items-center gap-[6px] rounded px-[11px] py-[5px] text-[12px]",
                active
                  ? "border border-black bg-[#F3F5AE] font-semibold text-[#1D1C1C]"
                  : "border-grey3 hover:bg-grey1 border bg-white text-[#4a4747]",
              )}
            >
              {active && <FolderIcon />}
              {f.name}
            </button>
          );
        })}
      </div>
      <div className="flex flex-col gap-[3px]">
        <div className="flex gap-[7px]">
          <input
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onAddFolder();
            }}
            placeholder={t("libraryPage:fileUploadNewFolder")}
            className={twMerge(
              "min-w-0 flex-1 rounded border px-[10px] py-2 text-[12.5px] outline-none",
              !isNewFolderNameValid && newFolderName.length
                ? "border-red-500"
                : "border-grey3",
            )}
          />
          <button
            type="button"
            onClick={onAddFolder}
            disabled={!isNewFolderNameValid}
            className="border-grey3 hover:bg-grey1 rounded border bg-white px-[14px] py-2 text-[12.5px] font-medium text-[#1D1C1C] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t("libraryPage:fileUploadAddFolder")}
          </button>
        </div>
        {!isNewFolderNameValid && !!newFolderName.length && (
          <span className="text-[11px] font-normal text-red-500">
            {t("libraryPage:folderNameInvalidChars")}
          </span>
        )}
      </div>
    </div>
  );
};
